import React, { useState } from 'react';
import {
  ClockIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  CloudIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAppStore, formatHistoryTime, HistoryItem } from '../store/appStore';
import { formatCardNumber } from '../utils/generators';

const History: React.FC = () => {
  const [appState, appStore] = useAppStore();
  const [copySuccess, setCopySuccess] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleCopy = async (item: HistoryItem) => {
    const text = `${item.cardNumber}|${item.expiry.replace('/', '|')}|${item.cvv}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(item.id);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch {
      // ignore
    }
  };

  const handleCopyFormatted = async (item: HistoryItem) => {
    const text = `卡号: ${formatCardNumber(item.cardNumber)}\n有效期: ${item.expiry}\nCVV: ${item.cvv}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(item.id + '-formatted');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch {
      // ignore
    }
  };

  const handleDelete = (id: string) => {
    appStore.deleteHistory(id);
  };

  const handleClearAll = () => {
    appStore.clearHistory();
    setShowClearConfirm(false);
  };

  const handleRefresh = () => {
    appStore.loadHistoryFromCloud();
  };

  const { history, historyLoading } = appState;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <ClockIcon className="h-6 w-6 mr-2 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">生成历史</h2>
            <span className="ml-3 px-2.5 py-0.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
              {history.length} 条记录
            </span>
            {appState.settings.useCloudSync && (
              <span className="ml-2 flex items-center text-xs text-green-600 dark:text-green-400">
                <CloudIcon className="h-4 w-4 mr-1" />
                云同步
              </span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={appState.settings.useCloudSync}
                onChange={(e) => appStore.updateSettings({ useCloudSync: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">云同步</span>
            </label>

            <button
              onClick={handleRefresh}
              disabled={historyLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
              刷新
            </button>

            {history.length > 0 && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                清空
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">确认清空</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              确定要清空所有历史记录吗？此操作无法撤销。
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                onClick={handleClearAll}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History List */}
      {history.length === 0 ? (
        <div className="card text-center py-12">
          <CreditCardIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">暂无历史记录</h3>
          <p className="text-gray-500 dark:text-gray-400">
            生成虚拟卡后，记录会自动保存在这里
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4">
          {history.map((item) => (
            <div
              key={item.id}
              className="card hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                {/* Card Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded">
                      {item.cardType}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatHistoryTime(item.timestamp)}
                    </span>
                  </div>
                  
                  <div className="font-mono text-base sm:text-lg text-gray-900 dark:text-white mb-1 truncate">
                    {formatCardNumber(item.cardNumber)}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>有效期: <span className="font-medium">{item.expiry}</span></span>
                    <span>CVV: <span className="font-medium">{item.cvv}</span></span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 sm:flex-shrink-0">
                  <button
                    onClick={() => handleCopy(item)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      copySuccess === item.id
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    title="复制为管道格式"
                  >
                    {copySuccess === item.id ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        已复制
                      </>
                    ) : (
                      <>
                        <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                        复制
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleCopyFormatted(item)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      copySuccess === item.id + '-formatted'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    title="复制格式化信息"
                  >
                    {copySuccess === item.id + '-formatted' ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      '格式化'
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="删除"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tips */}
      <div className="card bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
              <ClockIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">历史记录说明</h3>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              <li>• 最多保存 100 条历史记录</li>
              <li>• 数据仅保存在本地浏览器中</li>
              <li>• 清除浏览器数据会删除历史记录</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default History;
